import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiMessageSquare } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';

interface Remark {
  _id: string;
  text: string;
  adminName: string;
  createdAt: string;
}

type ScrollTarget = { kind: 'bottom' } | { kind: 'anchor'; id: string; offset: number };

function mergeRemarks(previous: Remark[], incoming: Remark[]): Remark[] {
  const unique = new Map(previous.map(remark => [remark._id, remark]));
  for (const remark of incoming) unique.set(remark._id, remark);
  return [...unique.values()].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || (a._id < b._id ? -1 : a._id > b._id ? 1 : 0));
}

export default function UserRemarks({ userId }: { userId: string }) {
  const [remarks, setRemarks] = useState<Remark[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const retryCursor = useRef<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const mounted = useRef(true);
  const submitting = useRef(false);
  const fetching = useRef(false);
  const requestId = useRef(0);
  const viewport = useRef<HTMLDivElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const scrollTarget = useRef<ScrollTarget | null>(null);
  const focusComposer = useRef(false);
  const atBottom = useRef(true);
  const viewportSize = useRef({ width: 0, height: 0 });
  const readingAnchor = useRef<ScrollTarget | null>(null);

  function scrollToLatest() {
    if (viewport.current) viewport.current.scrollTop = viewport.current.scrollHeight;
    atBottom.current = true;
  }

  function readingPosition(): ScrollTarget {
    const element = viewport.current;
    if (!element) return { kind: 'bottom' };
    const top = element.getBoundingClientRect().top;
    const anchor = [...element.querySelectorAll<HTMLElement>('[data-remark-id]')]
      .find(item => item.getBoundingClientRect().bottom > top);
    return anchor ? { kind: 'anchor', id: anchor.dataset.remarkId!, offset: anchor.getBoundingClientRect().top - top } : { kind: 'bottom' };
  }

  function restoreAnchor(target: ScrollTarget) {
    const element = viewport.current;
    if (!element || target.kind !== 'anchor') return;
    const anchor = [...element.querySelectorAll<HTMLElement>('[data-remark-id]')].find(item => item.dataset.remarkId === target.id);
    if (anchor) element.scrollTop += anchor.getBoundingClientRect().top - element.getBoundingClientRect().top - target.offset;
  }

  useLayoutEffect(() => {
    const element = viewport.current;
    const target = scrollTarget.current;
    scrollTarget.current = null;
    if (element && target) {
      if (target.kind === 'bottom') scrollToLatest();
      else {
        restoreAnchor(target);
        atBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 24;
      }
    }
    if (element) {
      viewportSize.current = { width: element.clientWidth, height: element.clientHeight };
      readingAnchor.current = readingPosition();
    }
    if (!saving && focusComposer.current) {
      focusComposer.current = false;
      textarea.current?.focus({ preventScroll: true });
    }
  }, [remarks, saving]);

  useEffect(() => {
    if (!viewport.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const element = viewport.current;
      if (!element) return;
      viewportSize.current = { width: element.clientWidth, height: element.clientHeight };
      if (atBottom.current) scrollToLatest();
      else if (readingAnchor.current) restoreAnchor(readingAnchor.current);
    });
    observer.observe(viewport.current);
    return () => observer.disconnect();
  }, []);

  async function load(before: string | null) {
    if (fetching.current) return;
    fetching.current = true;
    const currentRequest = ++requestId.current;
    retryCursor.current = before;
    setLoading(true);
    setError('');
    try {
      const query = before ? `before=${encodeURIComponent(before)}` : 'page=1';
      const result = await apiRequest<{ success: boolean; data: Remark[]; hasMore: boolean; nextCursor?: string | null }>(`/api/admin/users/${encodeURIComponent(userId)}/remarks?${query}`);
      if (!result.success) throw new Error('Unable to load remarks.');
      if (!mounted.current || currentRequest !== requestId.current) return;
      scrollTarget.current = before ? readingPosition() : { kind: 'bottom' };
      setRemarks(previous => mergeRemarks(previous, result.data));
      setHasMore(result.hasMore);
      setCursor(result.nextCursor ?? result.data[result.data.length - 1]?._id ?? null);
    } catch {
      if (mounted.current && currentRequest === requestId.current) setError('Unable to load remarks. Please retry.');
    } finally {
      if (currentRequest === requestId.current) {
        fetching.current = false;
        if (mounted.current) setLoading(false);
      }
    }
  }

  useEffect(() => {
    mounted.current = true;
    void load(null);
    return () => { mounted.current = false; requestId.current += 1; fetching.current = false; };
    // The parent keys this component by user ID to isolate drafts and requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function save() {
    if (!text.trim() || submitting.current) return;
    submitting.current = true;
    setSaving(true);
    setSaveError('');
    try {
      const result = await apiRequest<{ success: boolean; data: Remark }>(`/api/admin/users/${encodeURIComponent(userId)}/remarks`, {
        method: 'POST', body: JSON.stringify({ text: text.trim() }),
      });
      if (!result.success) throw new Error('Unable to save remark.');
      if (!mounted.current) return;
      setText('');
      scrollTarget.current = { kind: 'bottom' };
      // Append the saved record without discarding already loaded history or shifting its cursor.
      setRemarks(previous => mergeRemarks(previous, [result.data]));
    } catch {
      if (mounted.current) setSaveError('Unable to save remark. Your draft is kept; please retry.');
    } finally {
      submitting.current = false;
      if (mounted.current) { focusComposer.current = true; setSaving(false); }
    }
  }

  return <div className="um-remarks-pane">
    <div className="um-card um-remarks-card">
      <div className="um-card-header"><FiMessageSquare className="um-card-icon" /><h3>Admin Remarks</h3>
        {remarks.length > 0 && <button type="button" className="um-remark-latest" onClick={scrollToLatest}>Latest ↓</button>}
      </div>
      <div ref={viewport} className="um-remark-scroll" role="region" aria-label="Remark timeline" tabIndex={0}
        onScroll={() => {
          const element = viewport.current;
          // A resize can emit scroll before ResizeObserver; keep the pre-resize reading state.
          if (!element || element.clientWidth !== viewportSize.current.width || element.clientHeight !== viewportSize.current.height) return;
          atBottom.current = element.scrollHeight - element.scrollTop - element.clientHeight < 24;
          readingAnchor.current = readingPosition();
        }}>
        <div className="um-remark-history-control">
          {error ? <p role="alert">{error} <button type="button" disabled={loading} onClick={() => load(retryCursor.current)}>Retry</button></p>
            : hasMore ? <button type="button" disabled={loading || !cursor} onClick={() => load(cursor)}>{loading ? 'Loading older remarks…' : 'Load older remarks'}</button>
            : remarks.length > 0 ? <span>Beginning of history</span> : null}
        </div>
        {loading && remarks.length === 0 && <div className="um-empty">Loading remarks…</div>}
        {!loading && !error && remarks.length === 0 && <div className="um-empty">No remarks yet.</div>}
        <div className="um-ban-timeline" aria-live="polite" aria-relevant="additions">
          {remarks.map((remark, index) => <div key={remark._id} data-remark-id={remark._id} className="um-ban-timeline-item">
            <div className="um-ban-timeline-line-container">
              <div className="um-ban-timeline-dot"><FiMessageSquare /></div>
              {index < remarks.length - 1 && <div className="um-ban-timeline-line" />}
            </div>
            <div className="um-ban-timeline-content">
              <div className="um-ban-timeline-header"><strong>{remark.adminName}</strong><time className="um-ban-timeline-time" dateTime={remark.createdAt}>{new Date(remark.createdAt).toLocaleString()}</time></div>
              <p className="um-remark-text">{remark.text}</p>
            </div>
          </div>)}
        </div>
      </div>
      <form className="um-remark-form" onSubmit={event => { event.preventDefault(); void save(); }}>
        <label htmlFor="user-remark">Add an internal remark</label>
        <textarea ref={textarea} id="user-remark" className="um-ban-textarea" rows={2} maxLength={5000} value={text} disabled={saving}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) {
              event.preventDefault();
              void save();
            }
          }}
          onChange={event => setText(event.target.value)} placeholder="Customer follow-up, support context or important notes…" />
        <div className="um-remark-actions"><span>{text.length}/5000 · Enter to send · Shift+Enter for a new line · Admins only</span>
          <button type="submit" className="um-ban-action-btn success" disabled={saving || !text.trim()}>{saving ? 'Saving…' : 'Add remark'}</button>
        </div>
        {saveError && <p role="alert">{saveError}</p>}
      </form>
    </div>
  </div>;
}
